import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Lightbox } from "react-modal-image";
import {
  MAX_REFERENCE_PHOTOS,
  requestPhotoUploadSlots,
  uploadPhotoToSlot
} from "../../../../../services/checklistPhotoService";

const TILE = 88;

const tileSx = {
  borderRadius: "4px",
  height: TILE,
  overflow: "hidden",
  position: "relative",
  width: TILE,
  flexShrink: 0
};

const removeButtonSx = {
  backgroundColor: "rgba(0,0,0,0.6)",
  color: "#FFFFFF",
  height: 22,
  p: 0,
  position: "absolute",
  right: 4,
  top: 4,
  width: 22,
  "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" }
};

const hintSx = {
  color: "rgba(0,0,0,0.6)",
  fontFamily: "Roboto, sans-serif",
  fontSize: 12,
  letterSpacing: "0.4px"
};

let pendingSeed = 0;

/**
 * The photos of one checklist entry: its reference photos in the checklist
 * editor, or the photos of an answer while filling the checklist out. A photo
 * uploads as soon as it is added, under `uploadNodeId` (the node the backend
 * resolves its link by: the checklist, or the in-progress report); only
 * uploads that succeeded reach `onChange`. A failed one stays here with its
 * error until it is retried or removed.
 *
 * @param {string} uploadNodeId empty while that node doesn't exist yet
 * @param {() => Promise<string>} [resolveUploadNodeId] asked when there is no
 *   `uploadNodeId` yet (e.g. a report saved since the dialog opened); with
 *   it, adding stays on and the picked photos wait for its answer
 * @param {string} missingNodeIdHint shown while there is no node to upload to
 * @param {Array} photos the saved photos ({fileid, …, url?, previewUrl?})
 * @param {(update: (photos: Array) => Array) => void} onChange
 * @param {(uploading: boolean) => void} onUploadingChange
 */
export default function ChecklistEntryPhotos({
  uploadNodeId,
  resolveUploadNodeId,
  missingNodeIdHint,
  photos = [],
  onChange,
  onUploadingChange,
  invalid = false,
  disabled = false
}) {
  const inputRef = useRef(null);
  const [pending, setPending] = useState([]);
  const [limitHint, setLimitHint] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");
  const [nodeIdMissing, setNodeIdMissing] = useState(false);

  const uploadingCount = pending.filter(item => item.status === "uploading").length;
  const usedSlots = photos.length + uploadingCount;
  const canAdd =
    (Boolean(uploadNodeId) || Boolean(resolveUploadNodeId)) &&
    !disabled &&
    usedSlots < MAX_REFERENCE_PHOTOS;

  useEffect(() => {
    onUploadingChange?.(uploadingCount > 0);
  }, [uploadingCount > 0]);

  const patchPending = (key, patch) =>
    setPending(current =>
      current.map(item => (item.key === key ? { ...item, ...patch } : item))
    );

  const upload = async items => {
    let nodeId = uploadNodeId;
    if (!nodeId && resolveUploadNodeId) {
      try {
        nodeId = await resolveUploadNodeId();
      } catch (error) {
        nodeId = "";
      }
    }
    if (!nodeId) {
      // Nothing to upload under yet: these picks are dropped, the hint says why.
      setNodeIdMissing(true);
      items.forEach(item => URL.revokeObjectURL(item.previewUrl));
      const keys = new Set(items.map(item => item.key));
      setPending(current => current.filter(item => !keys.has(item.key)));
      return;
    }
    setNodeIdMissing(false);
    let slots;
    try {
      slots = await requestPhotoUploadSlots(nodeId, items.map(item => item.file));
    } catch (error) {
      items.forEach(item => patchPending(item.key, { status: "error" }));
      return;
    }
    await Promise.all(
      items.map(async (item, index) => {
        try {
          const stored = await uploadPhotoToSlot(slots[index], item.file, progress =>
            patchPending(item.key, { progress })
          );
          setPending(current => current.filter(candidate => candidate.key !== item.key));
          onChange(current => [...current, { ...stored, previewUrl: item.previewUrl }]);
        } catch (error) {
          patchPending(item.key, { status: "error", progress: 0 });
        }
      })
    );
  };

  const handleFiles = event => {
    const picked = Array.from(event.target.files || []).filter(file =>
      (file.type || "").startsWith("image/")
    );
    event.target.value = "";
    const room = MAX_REFERENCE_PHOTOS - usedSlots;
    setLimitHint(picked.length > room);
    const accepted = picked.slice(0, Math.max(0, room));
    if (!accepted.length) return;

    const items = accepted.map(file => {
      // A local preview until the dialog is reopened from the server's data;
      // kept on the saved photo, so only a discarded upload frees its own.
      const previewUrl = URL.createObjectURL(file);
      return {
        key: `pending-${Date.now()}-${pendingSeed++}`,
        file,
        previewUrl,
        progress: 0,
        status: "uploading"
      };
    });
    setPending(current => [...current, ...items]);
    upload(items);
  };

  const retry = item => {
    if (usedSlots >= MAX_REFERENCE_PHOTOS) {
      setLimitHint(true);
      return;
    }
    patchPending(item.key, { status: "uploading", progress: 0 });
    upload([item]);
  };

  const removePending = item => {
    URL.revokeObjectURL(item.previewUrl);
    setPending(current => current.filter(candidate => candidate.key !== item.key));
  };

  const removePhoto = fileid => {
    setLimitHint(false);
    onChange(current => current.filter(photo => photo.fileid !== fileid));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ alignItems: "center", display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          color={invalid ? "error" : "primary"}
          startIcon={<AddAPhotoOutlinedIcon />}
          disabled={!canAdd}
          onClick={() => inputRef.current?.click()}
          sx={{ letterSpacing: "1.25px" }}
        >
          Fotos hinzufügen
        </Button>
        <Typography sx={{ ...hintSx, fontVariantNumeric: "tabular-nums" }}>
          {`${usedSlots}/${MAX_REFERENCE_PHOTOS}`}
        </Typography>
        {/* image/* lets a phone offer the camera as well as the gallery. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFiles}
        />
      </Box>

      {(!uploadNodeId && !resolveUploadNodeId) || nodeIdMissing ? (
        <Typography sx={{ ...hintSx, color: nodeIdMissing ? "#FF3D00" : hintSx.color }}>
          {missingNodeIdHint}
        </Typography>
      ) : null}
      {limitHint ? (
        <Typography sx={{ ...hintSx, color: "#FF3D00" }}>
          {`Maximal ${MAX_REFERENCE_PHOTOS} Fotos pro Eintrag.`}
        </Typography>
      ) : null}

      {photos.length || pending.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {photos.map(photo => {
            const src = photo.previewUrl || photo.url;
            return (
              <Box key={photo.fileid} sx={{ ...tileSx, backgroundColor: "#F2F4F7" }}>
                {src ? (
                  <Box
                    component="img"
                    src={src}
                    alt=""
                    onClick={() => setLightboxImage(src)}
                    sx={{ cursor: "zoom-in", height: "100%", objectFit: "cover", width: "100%" }}
                  />
                ) : null}
                {!disabled ? (
                  <IconButton
                    aria-label="Foto entfernen"
                    onClick={() => removePhoto(photo.fileid)}
                    sx={removeButtonSx}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null}
              </Box>
            );
          })}

          {pending.map(item => (
            <Box
              key={item.key}
              sx={{
                ...tileSx,
                border: item.status === "error" ? "2px solid #FF3D00" : "none"
              }}
            >
              <Box
                component="img"
                src={item.previewUrl}
                alt=""
                sx={{ height: "100%", objectFit: "cover", opacity: 0.45, width: "100%" }}
              />
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  inset: 0,
                  justifyContent: "center",
                  position: "absolute"
                }}
              >
                {item.status === "uploading" ? (
                  <CircularProgress
                    size={32}
                    variant={item.progress ? "determinate" : "indeterminate"}
                    value={item.progress}
                    sx={{ color: "#0E3F51" }}
                  />
                ) : (
                  <>
                    <ErrorOutlineRoundedIcon sx={{ color: "#FF3D00", fontSize: 22 }} />
                    <Typography sx={{ ...hintSx, color: "#FF3D00", fontWeight: 500 }}>
                      Fehlgeschlagen
                    </Typography>
                    <IconButton
                      aria-label="Erneut hochladen"
                      onClick={() => retry(item)}
                      sx={{ color: "#0E3F51", p: 0.25 }}
                    >
                      <ReplayRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </>
                )}
              </Box>
              {item.status === "error" ? (
                <IconButton
                  aria-label="Foto verwerfen"
                  onClick={() => removePending(item)}
                  sx={removeButtonSx}
                >
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}

      {lightboxImage ? (
        <Lightbox
          large={lightboxImage}
          alt="Referenzfoto"
          hideZoom={false}
          onClose={() => setLightboxImage("")}
        />
      ) : null}
    </Box>
  );
}
